// Sources stay inert until their media is visible; original theme components retain controls.
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const connection = navigator.connection;
const controllers = new Set();
const initialized = new WeakSet();
const allowAutoplay = () => !reduceMotion.matches && !connection?.saveData && !/^(slow-)?2g$/.test(connection?.effectiveType || '');

export function observeVideo(root, resolveVideo) {
  let video;
  let visible = false;
  let userPaused = false;
  let explicitlyStarted = false;
  const load = () => {
    if (!video) {
      video = resolveVideo();
      video?.addEventListener('play', () => {
        if (!visible || document.hidden || userPaused || (!explicitlyStarted && !allowAutoplay())) video.pause();
      });
      video?.addEventListener('pause', () => {
        if (visible && !document.hidden && (explicitlyStarted || allowAutoplay())) userPaused = true;
      });
    }
    return video;
  };
  const pause = () => video?.pause();
  const play = () => {
    const media = load();
    if (media) media.play().catch(() => {});
  };
  const update = () => {
    if (!root.isConnected) { observer.disconnect(); controllers.delete(update); pause(); return; }
    if (!visible || document.hidden || userPaused || (!explicitlyStarted && !allowAutoplay())) pause();
    else play();
  };
  const observer = new IntersectionObserver(entries => {
    visible = entries[0].isIntersecting && entries[0].intersectionRatio > 0;
    update();
  }, { threshold: [0, 0.05] });
  observer.observe(root);
  controllers.add(update);
  return {
    play() { userPaused = false; explicitlyStarted = true; play(); },
    pause() { userPaused = true; pause(); },
    rememberUserChoice(paused) { userPaused = paused; explicitlyStarted = !paused; },
  };
}
function refresh() { controllers.forEach(update => update()); }
document.addEventListener('visibilitychange', refresh);
reduceMotion.addEventListener('change', refresh);
connection?.addEventListener('change', refresh);

export function attachVideoSource(video) {
  if (!video.hasAttribute('data-nv-source-loaded')) {
    const sources = [...video.querySelectorAll('source[data-nv-video-source]')];
    const mp4 = sources.filter(s => s.type === 'video/mp4').sort((a,b) => Number(a.dataset.height) - Number(b.dataset.height));
    const target = matchMedia('(max-width: 749px)').matches ? 480 : 720;
    const source = mp4.find(s => Number(s.dataset.height) >= target) || mp4.at(-1) || sources[0];
    if (source) {
      video.src = source.dataset.nvVideoSource;
      video.setAttribute('data-nv-source-loaded', '');
      video.load();
    }
  }
  return video;
}
function setup(root = document) {
  root.querySelectorAll('[data-nv-background-video]').forEach(panel => {
    if (initialized.has(panel)) return;
    initialized.add(panel);
    // Let the theme finish its initial empty load before supplying a source.
    customElements.whenDefined('video-background-component').then(() => {
      if (!panel.isConnected) return;
      const video = panel.querySelector('video');
      if (video) observeVideo(panel, () => attachVideoSource(video));
    });
  });
  root.querySelectorAll('deferred-media[data-nv-visible-autoplay]').forEach(panel => {
    if (initialized.has(panel)) return;
    initialized.add(panel);
    customElements.whenDefined('deferred-media').then(() => {
      if (!panel.isConnected) return;
      const controller = observeVideo(panel, () => {
        panel.loadContent(false);
        const video = panel.querySelector('video');
        if (video && !video.dataset.nvControlsSynced) {
          video.dataset.nvControlsSynced = 'true';
          const sync = () => { panel.isPlaying = !video.paused; panel.updatePlayPauseHint(!video.paused); };
          video.addEventListener('play', sync);
          video.addEventListener('pause', sync);
        }
        return video;
      });
      panel.addEventListener('click', event => {
        if (event.target.closest('[ref="deferredMediaPlayButton"]')) controller.play();
        else if (event.target.closest('[ref="toggleMediaButton"]')) {
          queueMicrotask(() => controller.rememberUserChoice(!!panel.querySelector('video')?.paused));
        }
      });
    });
  });
}
setup();
document.addEventListener('shopify:section:load', event => setup(event.target));
